<script setup lang="ts">
import SvgIcon from "~/components/Common/SvgIcon.vue";
import Tag from "~/components/Common/Tag.vue";
import { useFormats } from "~/composables/utils/useFormat";
import PanelActionsButton from "~/components/Common/Buttons/PanelActionsButton.vue";
import { useLearningPath } from "~/composables/learning-path/useLearningPath";
import Tooltip from "~/components/Common/Tooltip.vue";
import { TrainingFormatTags } from "~/utils/options/session-items.options";
import type { ILearningPathTrainingCard } from "~/utils/interfaces/learning-path-interfaces";

const props = defineProps<{
  index: number;
  training: ILearningPathTrainingCard;
  disabled: boolean;
}>();
const formats = useFormats();
const { cardPanel } = useLearningPath();

// The format lives on the training itself, so an LMS training is no longer forced to read "eLearning".
const formatTag = computed(() => (props.training.format ? TrainingFormatTags[props.training.format] : null));
</script>

<template>
  <div class="flex items-center gap-4 w-full">
    <!-- Drag Handle Icon -->
    <div v-if="!disabled" class="drag-handle">
      <SvgIcon name="reorganize" class="w-4 h-4 text-secondary-color" />
    </div>
    <div v-else class="drag-handle">
      <Tooltip :position="'top-right'" append-to-body minified>
        <template #img>
          <SvgIcon name="reorganize" class="w-4 h-4 text-secondary-color" />
        </template>
        <template #content>
          <div class="flex gap-2 max-w-[414px] break-words py-3 px-4 items-center">
            <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
            <span class="body_regular"
              >Vous ne pouvez pas ré-ordonner les formations d’un parcours qui a déjà des participants inscrits.</span
            >
          </div>
        </template>
      </Tooltip>
    </div>

    <!-- Card Content -->
    <div class="container-card">
      <div class="flex flex-row gap-[8px] w-full justify-between px-6 py-4">
        <div class="flex flex-col gap-[8px]">
          <!-- Header with Icon and Text -->
          <div class="flex gap-[16px] items-center">
            <div class="training_card__img_container">
              <img v-if="training.tpLogo" class="training_card__img" :src="training.tpLogo" alt="logo partenaire" />
              <div
                v-else
                class="w-[64px] h-[64px] bg-lightest-grey-color flex items-center justify-center rounded-[16px]"
              >
                <SvgIcon name="graduation_cap" class="h-[21px] w-[31px] text-medium-grey-color" />
              </div>
            </div>
            <div class="flex flex-col text-start flex-1 min-w-0">
              <span class="body_med text-primary-color trainings_text">{{ training.title }}</span>
              <span class="body_regular text-tertiary-color trainings_text">{{ training.trainingPartnerName }}</span>
            </div>
          </div>

          <!-- Tags Section -->
          <div class="flex gap-1 items-start flex-wrap">
            <Tag
              v-if="training.durationHours && Number.isFinite(training.durationHours)"
              :tag="{
                type: 'light_grey',
                value: `${formats.formatTo2Decimals(training.durationHours)}h`,
                logo: 'time_normal',
              }"
            >
            </Tag>
            <Tag v-if="formatTag" :tag="formatTag"> </Tag>
          </div>
        </div>
        <!-- Menu Button -->
        <div class="flex justify-center items-center" @click.stop>
          <PanelActionsButton :id="index" on-select dot-style :actions="cardPanel"> </PanelActionsButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "assets/scss/texts";

.container-card {
  @apply flex w-full;
  scrollbar-gutter: stable;
  border: 1px solid var(--grey-lightest);
  background: var(--white-main);
  border-radius: 18px;
  min-height: 130px;
  box-shadow: var(--shadow-soft);

  &:hover {
    box-shadow: var(--shadow-medium);
  }
}

.drag-handle {
  @apply cursor-grab;
}

.trainings_text {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.training_card__img_container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  min-height: 64px;
  min-width: 64px;
}

.menu-button {
  @apply w-6 h-6 rounded flex-shrink-0 cursor-pointer flex items-center justify-center;
  transition: background-color 0.2s;

  &:hover {
    background: var(--grey-lightest);
  }
}
</style>
